import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { isNewCarFuelType } from '@/apis/server/new-car/v1/utils';
import type {
  FilterValue,
} from '@/components/search-filter/filter-label-list/types'; // @suppress longLineCheck
import {
  AUTHORISED_DISTRIBUTORS,
  FILTER_TYPE_NAME_CATEGORY,
  FILTER_TYPE_NAME_COE_TYPE,
  FILTER_TYPE_NAME_COUNTRY,
  FILTER_TYPE_NAME_DEALER_TYPE,
  FILTER_TYPE_NAME_DEPRECIATION,
  FILTER_TYPE_NAME_DRIVE_RANGE,
  FILTER_TYPE_NAME_ENERGY_CONSUPTION,
  FILTER_TYPE_NAME_ENGINE_CAP,
  FILTER_TYPE_NAME_FUEL_CONSUMPTION,
  FILTER_TYPE_NAME_FUEL_TYPE,
  FILTER_TYPE_NAME_OMV,
  FILTER_TYPE_NAME_POWER_OUTPUT,
  FILTER_TYPE_NAME_PRICE_RANGE,
  FILTER_TYPE_NAME_TRANSIMISSION,
  PARALLEL_IMPORTERS,
} from '@/constants/global';

import {
  categoryToText,
  coeTypeToText,
  conditionToText,
  dealerTypeToText,
  fuelTypeToText,
  transmissionToText,
  vehicleTypeToText,
} from './configs';
import type { Hook } from './types';
import { isVehicleType } from './utils';

export const useFilterParams: Hook = (searchData) => {
  const [filterValues, setFilterValue] = useState<FilterValue[]>([]);
  const searchParams = useSearchParams();

  useEffect(() => {
    const result: FilterValue[] = [];

    const {
      category,
        coeCategory,
        country,
        dealerId,
        dealerType,
        fuelType,
        order,
        q,
        make,
        sortBy,
        transmission,
        vehicleType,
        page,
        limit,
        year,
        maxDepreciation,
        maxDriveRange,
        maxEnergyConsumption,
        maxEngineCapacity,
        maxFuelEconomy,
        maxOmv,
        maxPowerOutput,
        maxPrice,
        minDepreciation,
        minDriveRange,
        minEnergyConsumption,
        minEngineCapacity,
        minFuelEconomy,
        minOmv,
        minPowerOutput,
        minPrice,
    } = searchData.query ?? {};

    if (minPrice || maxPrice) {
      result.push({
        name: FILTER_TYPE_NAME_PRICE_RANGE,
        type: 'range',
        lowerValue: {
          text: minPrice ? `$${(+minPrice).toLocaleString('en-US')}` : '',
          value: minPrice ? +minPrice : '',
        },
        upperValue: {
          text: maxPrice ? `$${(+maxPrice).toLocaleString('en-US')}` : '',
          value: maxPrice ? +maxPrice : '',
        },
      });
    }

    if (minOmv && maxOmv) {
      result.push({
        name: FILTER_TYPE_NAME_OMV,
        type: 'choice',
        optionValue: {
          text: conditionToText['='],
          value: '=',
        },
        inputValue: minOmv.toString(),
        unit: {
          value: '$',
          type: 'prefix',
        },
      });
    } else if (minOmv && maxOmv === null) {
      result.push({
        name: FILTER_TYPE_NAME_OMV,
        type: 'choice',
        optionValue: {
          text: conditionToText['>'],
          value: '>',
        },
        inputValue: minOmv.toString(),
        unit: {
          value: '$',
          type: 'prefix',
        },
      });
    } else if (minOmv === null && maxOmv) {
      result.push({
        name: FILTER_TYPE_NAME_OMV,
        type: 'choice',
        optionValue: {
          text: conditionToText['<'],
          value: '<',
        },
        inputValue: maxOmv.toString(),
        unit: {
          value: '$',
          type: 'prefix',
        },
      });
    }

    if (minDepreciation || maxDepreciation) {
      result.push({
        name: FILTER_TYPE_NAME_DEPRECIATION,
        type: 'range',
        lowerValue: {
          text: minDepreciation ? `$${(+minDepreciation).toLocaleString('en-US')}/yr` : '',
          value: minDepreciation ? +minDepreciation : '',
        },
        upperValue: {
          text: maxDepreciation ? `$${(+maxDepreciation).toLocaleString('en-US')}/yr` : '',
          value: maxDepreciation ? +maxDepreciation : '',
        },
      });
    }

    if (transmission === 'A' || transmission === 'M') {
      result.push({
        name: FILTER_TYPE_NAME_TRANSIMISSION,
        type: 'select',
        optionValue: {
          text: transmissionToText[transmission],
          value: transmission,
        },
      });
    }

    if (minEngineCapacity && maxEngineCapacity) {
      result.push({
        name: FILTER_TYPE_NAME_ENGINE_CAP,
        type: 'select',
        optionValue: {
          text: `${(+minEngineCapacity).toLocaleString('en-US')}cc - ${(+maxEngineCapacity).toLocaleString('en-US')}cc`,
          value: `${minEngineCapacity}-${maxEngineCapacity}`,
        },
      });
    } else if (minEngineCapacity && maxEngineCapacity === null) {
      result.push({
        name: FILTER_TYPE_NAME_ENGINE_CAP,
        type: 'select',
        optionValue: {
          text: `>${(+minEngineCapacity).toLocaleString('en-US')}cc`,
          value: `>${minEngineCapacity}`,
        },
      });
    } else if (minEngineCapacity === null && maxEngineCapacity) {
      result.push({
        name: FILTER_TYPE_NAME_ENGINE_CAP,
        type: 'select',
        optionValue: {
          text: `<${(+maxEngineCapacity).toLocaleString('en-US')}cc`,
          value: `<${maxEngineCapacity}`,
        },
      });
    }

    if (
      category === AUTHORISED_DISTRIBUTORS
      || category === PARALLEL_IMPORTERS
      || category === 'a'
      || category === 'b') {
      result.push({
        name: FILTER_TYPE_NAME_CATEGORY,
        type: 'select',
        optionValue: {
          text: categoryToText[category],
          value: category,
        },
      });
    }

    if (dealerType === AUTHORISED_DISTRIBUTORS
      || dealerType === PARALLEL_IMPORTERS) {
      result.push({
        name: FILTER_TYPE_NAME_DEALER_TYPE,
        type: 'select',
        optionValue: {
          text: dealerTypeToText[dealerType],
          value: dealerType,
        },
      });
    }

    const coeType = searchParams.get('coe_category');

    if (coeType === 'a' || coeType === 'b') {
      result.push({
        name: FILTER_TYPE_NAME_COE_TYPE,
        type: 'select',
        optionValue: {
          text: coeTypeToText[coeType],
          value: coeType,
        },
      });
    }

    if (make) {
      result.push({
        name: 'Car Make',
        type: 'select',
        optionValue: {
          text: make,
          value: make,
        },
      });
    }

    if (year) {
      result.push({
        name: 'Available Year',
        type: 'select',
        optionValue: {
          text: year,
          value: year,
        },
      });
    }

    if (country) {
      result.push({
        name: FILTER_TYPE_NAME_COUNTRY,
        type: 'select',
        optionValue: {
          text: country,
          value: country,
        },
      });
    }

    if (isNewCarFuelType(fuelType)) {
      result.push({
        name: FILTER_TYPE_NAME_FUEL_TYPE,
        type: 'select',
        optionValue: {
          text: fuelTypeToText[fuelType],
          value: fuelType,
        },
      });
    }

    if (vehicleType && isVehicleType(vehicleType)) {
      vehicleType.split(',').forEach((v) => {
        result.push({
          name: 'Vehicle Type',
          type: 'checkbox',
          checkboxName: vehicleTypeToText[v],
          checkboxValue: v,
          isChecked: true,
        });
      });
    }

    if (minDriveRange || maxDriveRange) {
      result.push({
        name: FILTER_TYPE_NAME_DRIVE_RANGE,
        type: 'range',
        lowerValue: {
          text: minDriveRange ? `${(+minDriveRange).toLocaleString('en-US')} km` : '',
          value: minDriveRange ? +minDriveRange : '',
        },
        upperValue: {
          text: maxDriveRange ? `${(+maxDriveRange).toLocaleString('en-US')} km` : '',
          value: maxDriveRange ? +maxDriveRange : '',
        },
      });
    }

    if (minFuelEconomy && maxFuelEconomy) {
      result.push({
        name: FILTER_TYPE_NAME_FUEL_CONSUMPTION,
        type: 'choice',
        optionValue: {
          text: conditionToText['='],
          value: '=',
        },
        inputValue: minFuelEconomy.toString(),
        unit: {
          value: 'km/L',
          type: 'suffix',
        },
      });
    } else if (minFuelEconomy && maxFuelEconomy === null) {
      result.push({
        name: FILTER_TYPE_NAME_FUEL_CONSUMPTION,
        type: 'choice',
        optionValue: {
          text: conditionToText['>'],
          value: '>',
        },
        inputValue: minFuelEconomy.toString(),
        unit: {
          value: 'km/L',
          type: 'suffix',
        },
      });
    } else if (minFuelEconomy === null && maxFuelEconomy) {
      result.push({
        name: FILTER_TYPE_NAME_FUEL_CONSUMPTION,
        type: 'choice',
        optionValue: {
          text: conditionToText['<'],
          value: '<',
        },
        inputValue: maxFuelEconomy.toString(),
        unit: {
          value: 'km/L',
          type: 'suffix',
        },
      });
    }

    if (minPowerOutput && maxPowerOutput) {
      result.push({
        name: FILTER_TYPE_NAME_POWER_OUTPUT,
        type: 'choice',
        optionValue: {
          text: conditionToText['='],
          value: '=',
        },
        inputValue: minPowerOutput.toString(),
        unit: {
          value: 'bhp',
          type: 'suffix',
        },
      });
    } else if (minPowerOutput && maxPowerOutput === null) {
      result.push({
        name: FILTER_TYPE_NAME_POWER_OUTPUT,
        type: 'choice',
        optionValue: {
          text: conditionToText['>'],
          value: '>',
        },
        inputValue: minPowerOutput.toString(),
        unit: {
          value: 'bhp',
          type: 'suffix',
        },
      });
    } else if (minPowerOutput === null && maxPowerOutput) {
      result.push({
        name: FILTER_TYPE_NAME_POWER_OUTPUT,
        type: 'choice',
        optionValue: {
          text: conditionToText['<'],
          value: '<',
        },
        inputValue: maxPowerOutput.toString(),
        unit: {
          value: 'bhp',
          type: 'suffix',
        },
      });
    }

    if (minEnergyConsumption && maxEnergyConsumption) {
      result.push({
        name: FILTER_TYPE_NAME_ENERGY_CONSUPTION,
        type: 'choice',
        optionValue: {
          text: conditionToText['='],
          value: '=',
        },
        inputValue: minEnergyConsumption.toString(),
        unit: {
          value: 'kWh',
          type: 'suffix',
        },
      });
    } else if (minEnergyConsumption && maxEnergyConsumption === null) {
      result.push({
        name: FILTER_TYPE_NAME_ENERGY_CONSUPTION,
        type: 'choice',
        optionValue: {
          text: conditionToText['>'],
          value: '>',
        },
        inputValue: minEnergyConsumption.toString(),
        unit: {
          value: 'kWh',
          type: 'suffix',
        },
      });
    } else if (minEnergyConsumption === null && maxEnergyConsumption) {
      result.push({
        name: FILTER_TYPE_NAME_ENERGY_CONSUPTION,
        type: 'choice',
        optionValue: {
          text: conditionToText['<'],
          value: '<',
        },
        inputValue: maxEnergyConsumption.toString(),
        unit: {
          value: 'kWh',
          type: 'suffix',
        },
      });
    }

    setFilterValue(result);
  }, [searchParams]);

  return [
    filterValues,
    setFilterValue,
  ];
};
